import { Component, h, Element, Host, Prop, Watch, State } from "@stencil/core";
import { debounce } from "../../utils/debounce";

/**
 * Webinar component - the layout changes depending on the viewport
 * @slot header-text - The copy text in the header of the webinar
 * @slot info - details about the webinar (should be ux-iws-webinar-info)
 * @slot description - The description block of the webinar
 * @slot speaker - The speakers of the webinar (should be ux-iws-webinar-speaker elements)
 * @slot form - The registration form of the webinar
 */
@Component({
    tag: "ux-iws-webinar",
    styleUrl: "webinar.scss",
    shadow: true,
})
export class Webinar {
    @Element() domNode: HTMLUxIwsWebinarElement;

    /**
     * The title for the header
     */
    @Prop() headerTitle: string;

    /**
     * The background image for the header
     */
    @Prop() headerImage: string;

    /**
     * The title for the speakers section
     */
    @Prop() speakersTitle: string;

    /**
     * use the dark design for the header
     */
    @Prop({
        reflect: true,
    })
    dark: boolean = false;

    /**
     * Info that will be injected into the form as a hidden field
     */
    @Prop() webinarDataUid: string;

    @State() hasInfo: boolean = false;

    private descriptionRef: HTMLDivElement;
    private speakersRef: HTMLDivElement;
    private headerRef: HTMLDivElement;
    private speakers: string[];

    componentWillLoad() {
        this.hasInfo =
            this.domNode.querySelector("ux-iws-webinar-info") !== null;
        this.speakers = Array.from(
            this.domNode.querySelectorAll<HTMLUxIwsWebinarSpeakerElement>(
                "ux-iws-webinar-speaker[slot='speaker']"
            )
        ).map((el: HTMLUxIwsWebinarSpeakerElement) =>
            el.outerHTML.replace(/slot="speaker"/g, "")
        );
        this.appendDataUid();
    }

    componentDidLoad() {
        this.resize();
        window.addEventListener("resize", () => this.resize());
        this.headerImageChanged();
    }

    disconnectedCallback() {
        window.removeEventListener("resize", () => this.resize());
    }

    /**
     * webinarDataUid is needed by the BE to identify the webinar in the form submission
     * to display the correct infos in the success message
     */
    private appendDataUid() {
        const targetInput = Array.from(
            this.domNode.querySelectorAll("form input[type='hidden']")
        ).find((el) => {
            // find the input with the additional-info id like "webinarTestform-968533-additionalinfo"
            return /.+-\d+-additionalinfo/.test(el.id);
        }) as HTMLInputElement;
        if (!targetInput) {
            console.error("No additional-info input found");
            return;
        }
        try {
            const jsonContent = JSON.parse(targetInput.value);
            targetInput.value = JSON.stringify({
                ...jsonContent,
                webinarDataUid: this.webinarDataUid,
            });
        } catch (e) {
            console.error("Error setting value for additional-info input", e);
        }
    }

    @Watch("headerImage")
    headerImageChanged() {
        this.domNode.style.setProperty(
            "--ux-iws-webinar-header-image",
            `url(${this.headerImage})`
        );
    }

    /**
     * this calculation is needed to wrap the form next to description and speakers in the biggest breakpoint
     */
    private resize = debounce(() => {
        const descriptionHeight = this.descriptionRef.clientHeight;
        const speakersHeight = this.speakersRef.clientHeight;
        const totalHeight = descriptionHeight + speakersHeight + 2;
        this.domNode.style.setProperty(
            "--ux-iws-webinar-description-and-speakers-height",
            `${totalHeight}px`
        );
        const headerHeight = this.headerRef.clientHeight;
        this.domNode.style.setProperty(
            "--ux-iws-webinar-header-height",
            `${headerHeight}px`
        );
    }, 10);

    render() {
        return (
            <Host>
                <div class="header" ref={(el) => (this.headerRef = el)}>
                    <div class="header__image">
                        <h1 class="header__title headline headline--large">
                            {this.headerTitle}
                        </h1>
                        <div class="header__text">
                            <slot name="header-text"></slot>
                        </div>
                    </div>
                    {this.hasInfo && (
                        <div class="header__info">
                            <div class="header__info__content">
                                <slot name="info"></slot>
                            </div>
                        </div>
                    )}
                </div>
                <div class="flex-container">
                    <div
                        class="description"
                        ref={(el) => (this.descriptionRef = el)}
                    >
                        <slot name="description"></slot>
                    </div>
                    <div class="speakers" ref={(el) => (this.speakersRef = el)}>
                        {this.speakersTitle && (
                            <h2 class="speakers__title headline headline--small">
                                {this.speakersTitle}
                            </h2>
                        )}
                        <div class="speakers__mobile">
                            {this.speakers.length > 0 && (
                                <ux-slider no-arrows slides-auto-height>
                                    {this.speakers.map((speaker) => (
                                        <ux-slider-slide
                                            innerHTML={speaker}
                                        ></ux-slider-slide>
                                    ))}
                                    <ux-slider-pagination
                                        slot="pagination"
                                        bottom-pagination
                                    ></ux-slider-pagination>
                                </ux-slider>
                            )}
                        </div>
                        <div class="speakers__desktop">
                            <slot name="speaker"></slot>
                        </div>
                    </div>
                    <div class="form">
                        <div class="form__shadow">
                            <slot name="form"></slot>
                        </div>
                    </div>
                </div>
            </Host>
        );
    }
}
